import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './AssignmentDetailView.css';

function AssignmentDetailView({ assignment, student, grade, submission, onClose }) {
  const [showDescription, setShowDescription] = useState(false);
  const [learningMaterials, setLearningMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    if (assignment?.id && student?.classId) {
      loadLearningMaterials(student.classId, assignment.id);
    }
  }, [assignment?.id, student?.classId]);

  const loadLearningMaterials = async (classId, assignmentId) => {
    setLoadingMaterials(true);
    try {
      // Try loading from assignment-level learning materials subcollection
      try {
        const materialsSub = collection(db, 'classes', classId, 'assignments', assignmentId, 'learningMaterials');
        const materialsSnap = await getDocs(materialsSub);
        if (!materialsSnap.empty) {
          const materials = materialsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setLearningMaterials(materials);
          return;
        }
      } catch (e) { /* fall through to class-level */ }

      // Fallback: load class-level learning materials
      try {
        const classMaterialsSub = collection(db, 'classes', classId, 'learningMaterials');
        const classMaterialsSnap = await getDocs(classMaterialsSub);
        const materials = classMaterialsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setLearningMaterials(materials);
      } catch (e) {
        setLearningMaterials([]);
      }
    } catch (error) {
      console.error('Error loading learning materials:', error);
      setLearningMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const getMaterialTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return '📄';
      case 'document': return '📃';
      case 'image': return '🖼️';
      case 'video': return '🎥';
      case 'presentation': return '📊';
      case 'link': return '🔗';
      default: return '📎';
    }
  };

  if (!assignment) {
    return (
      <div className="assignment-detail-overlay" onClick={onClose}>
        <div className="assignment-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Assignment Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-content">
            <p>No assignment information available</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusInfo = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const timeDiff = due - now;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 0) {
      return { status: 'overdue', days: Math.abs(daysDiff), color: '#dc3545', text: `${Math.abs(daysDiff)} days overdue` };
    } else if (daysDiff === 0) {
      return { status: 'due-today', days: 0, color: '#ffc107', text: 'Due today' };
    } else if (daysDiff <= 3) {
      return { status: 'due-soon', days: daysDiff, color: '#fd7e14', text: `Due in ${daysDiff} days` };
    } else {
      return { status: 'on-time', days: daysDiff, color: '#28a745', text: `Due in ${daysDiff} days` };
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'homework': return '📚';
      case 'quiz': return '📝';
      case 'test': return '📋';
      case 'project': return '🎯';
      case 'presentation': return '📊';
      case 'lab': return '🔬';
      case 'essay': return '📄';
      default: return '📝';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const statusInfo = assignment.dueDate ? getStatusInfo(assignment.dueDate) : null;

  return (
    <div className="assignment-detail-overlay" onClick={onClose}>
      <div className="assignment-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assignment Details</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          {/* Assignment Overview */}
          <div className="section">
            <h4>Overview</h4>
            <div className="assignment-overview">
              <div className="overview-header">
                <div className="assignment-icon">
                  {getTypeIcon(assignment.assignmentType)}
                </div>
                <div className="overview-info">
                  <h3>{assignment.title || 'Untitled Assignment'}</h3>
                  <div className="assignment-meta">
                    <span className="type-badge">{assignment.assignmentType || 'Assignment'}</span>
                    {assignment.priority && (
                      <span 
                        className="priority-badge" 
                        style={{ backgroundColor: getPriorityColor(assignment.priority) }}
                      >
                        {assignment.priority.charAt(0).toUpperCase() + assignment.priority.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {statusInfo && (
                <div className="status-info">
                  <div className={`status-indicator ${statusInfo.status}`} style={{ backgroundColor: statusInfo.color }}>
                    {statusInfo.text}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Details */}
          <div className="section">
            <div className="section-header">
              <h4>Details</h4>
              <button 
                className="toggle-description"
                onClick={() => setShowDescription(!showDescription)}
              >
                {showDescription ? 'Hide' : 'Show'} Description
              </button>
            </div>
            
            <div className="details-grid">
              <div className="detail-item">
                <label>Type:</label>
                <span>{assignment.assignmentType || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Due Date:</label>
                <span>
                  {assignment.dueDate?.toDate?.() ? 
                    new Date(assignment.dueDate.toDate()).toLocaleDateString() : 
                    assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 
                    'No due date'
                  }
                </span>
              </div>
              <div className="detail-item">
                <label>Due Time:</label>
                <span>
                  {assignment.dueDate?.toDate?.() ? 
                    new Date(assignment.dueDate.toDate()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                    'No due time'
                  }
                </span>
              </div>
              <div className="detail-item">
                <label>Max Score:</label>
                <span>{assignment.maxScore || 'N/A'} points</span>
              </div>
              {assignment.weight && (
                <div className="detail-item">
                  <label>Weight:</label>
                  <span>{assignment.weight}% of grade</span>
                </div>
              )}
              {assignment.duration && (
                <div className="detail-item">
                  <label>Duration:</label>
                  <span>{assignment.duration}</span>
                </div>
              )}
            </div>

            {showDescription && assignment.description && (
              <div className="description-section">
                <h5>Description</h5>
                <div className="description-content">
                  <p>{assignment.description}</p>
                </div>
              </div>
            )}
          </div>

          {/* Student Information */}
          <div className="section">
            <h4>Student Information</h4>
            <div className="student-details">
              <div className="student-avatar">
                <span>👤</span>
              </div>
              <div className="student-info">
                <div className="student-name">{student?.fullName || 'N/A'}</div>
                <div className="student-meta">
                  <span>ID: {student?.studentId || 'N/A'}</span>
                  <span>Class: {student?.className || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Grade Information */}
          {grade && (
            <div className="section">
              <h4>Grade Information</h4>
              <div className="grade-summary">
                <div className="grade-display">
                  <div className="grade-score">
                    <span className="score">{grade.score}</span>
                    <span className="out-of">/ {grade.maxScore}</span>
                  </div>
                  <div className="grade-percentage">
                    {grade.percentage?.toFixed(1) || 'N/A'}%
                  </div>
                  <div className="letter-grade" style={{ 
                    backgroundColor: grade.percentage >= 90 ? '#28a745' : 
                                   grade.percentage >= 80 ? '#17a2b8' : 
                                   grade.percentage >= 70 ? '#ffc107' : 
                                   grade.percentage >= 60 ? '#fd7e14' : '#dc3545'
                  }}>
                    {grade.letterGrade || 'N/A'}
                  </div>
                </div>
                
                <div className="grade-details">
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status-badge ${grade.status || 'pending'}`}>
                      {grade.status ? grade.status.charAt(0).toUpperCase() + grade.status.slice(1) : 'Pending'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Graded Date:</label>
                    <span>
                      {grade.createdAt?.toDate?.() ? 
                        new Date(grade.createdAt.toDate()).toLocaleDateString() : 
                        'N/A'
                      }
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Teacher:</label>
                    <span>{grade.teacherName || 'N/A'}</span>
                  </div>
                  {grade.comments && (
                    <div className="detail-item full-width">
                      <label>Comments:</label>
                      <div className="grade-comments">
                        <p>{grade.comments}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submission Information */}
          {submission && !grade && (
            <div className="section">
              <h4>Submission Status</h4>
              <div className="grade-summary">
                <div className="grade-display">
                  <div className="grade-score">
                    <span className="score" style={{ fontSize: '1.5rem', color: submission.status === 'late' ? '#dc3545' : '#28a745' }}>
                      {submission.status ? submission.status.charAt(0).toUpperCase() + submission.status.slice(1) : 'Submitted'}
                    </span>
                  </div>
                </div>
                
                <div className="grade-details">
                  {submission.submittedAt && (
                    <div className="detail-item">
                      <label>Submitted:</label>
                      <span>
                        {submission.submittedAt?.toDate?.() ? 
                          new Date(submission.submittedAt.toDate()).toLocaleString() : 
                          new Date(submission.submittedAt).toLocaleString()
                        }
                      </span>
                    </div>
                  )}
                  {submission.startedAt && (
                    <div className="detail-item">
                      <label>Started:</label>
                      <span>
                        {submission.startedAt?.toDate?.() ? 
                          new Date(submission.startedAt.toDate()).toLocaleString() : 
                          new Date(submission.startedAt).toLocaleString()
                        }
                      </span>
                    </div>
                  )}
                  {submission.studentResponse && (
                    <div className="detail-item full-width">
                      <label>Student Response:</label>
                      <div className="grade-comments">
                        <p>{submission.studentResponse}</p>
                      </div>
                    </div>
                  )}
                  {submission.attachments && submission.attachments.length > 0 && (
                    <div className="detail-item full-width">
                      <label>Attached Files:</label>
                      <div>
                        {submission.attachments.map((att, idx) => (
                          <div key={idx} style={{ marginBottom: '4px' }}>
                            <span>📎 {att.fileName || 'File'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="section">
            <h4>Assignment Timeline</h4>
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-dot created"></div>
                <div className="timeline-content">
                  <div className="timeline-date">
                    {assignment.createdAt?.toDate?.() ? 
                      new Date(assignment.createdAt.toDate()).toLocaleDateString() : 
                      'Created'
                    }
                  </div>
                  <div className="timeline-title">Assignment Created</div>
                  <div className="timeline-description">
                    {assignment.title} was assigned to the class
                  </div>
                </div>
              </div>
              
              {assignment.dueDate && (
                <div className="timeline-item">
                  <div className={`timeline-dot ${statusInfo?.status || 'pending'}`} style={{ backgroundColor: statusInfo?.color }}></div>
                  <div className="timeline-content">
                    <div className="timeline-date">
                      {assignment.dueDate?.toDate?.() ? 
                        new Date(assignment.dueDate.toDate()).toLocaleDateString() : 
                        'Due Date'
                      }
                    </div>
                    <div className="timeline-title">Due Date</div>
                    <div className="timeline-description">
                      {statusInfo?.text || 'Assignment is due'}
                    </div>
                  </div>
                </div>
              )}
              
              {grade && (
                <div className="timeline-item">
                  <div className="timeline-dot completed"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">
                      {grade.createdAt?.toDate?.() ? 
                        new Date(grade.createdAt.toDate()).toLocaleDateString() : 
                        'Graded'
                      }
                    </div>
                    <div className="timeline-title">Assignment Graded</div>
                    <div className="timeline-description">
                      Score: {grade.score}/{grade.maxScore} ({grade.percentage?.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              )}
              
              {submission && !grade && (
                <div className="timeline-item">
                  <div className="timeline-dot completed" style={{ backgroundColor: submission.status === 'late' ? '#dc3545' : '#17a2b8' }}></div>
                  <div className="timeline-content">
                    <div className="timeline-date">
                      {submission.submittedAt?.toDate?.() ? 
                        new Date(submission.submittedAt.toDate()).toLocaleDateString() : 
                        submission.submittedAt ? new Date(submission.submittedAt).toLocaleDateString() : 'Submitted'
                      }
                    </div>
                    <div className="timeline-title">Assignment {submission.status === 'late' ? 'Submitted Late' : 'Submitted'}</div>
                    <div className="timeline-description">
                      {submission.status === 'started' ? 'Student started working' : `Student submitted${submission.status === 'late' ? ' (late)' : ''}`}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Requirements */}
          {assignment.requirements && (
            <div className="section">
              <h4>Requirements</h4>
              <div className="requirements-list">
                {Array.isArray(assignment.requirements) ? 
                  assignment.requirements.map((req, index) => (
                    <div key={index} className="requirement-item">
                      <span className="requirement-bullet">•</span>
                      <span>{req}</span>
                    </div>
                  )) : 
                  <div className="requirement-item">
                    <span className="requirement-bullet">•</span>
                    <span>{assignment.requirements}</span>
                  </div>
                }
              </div>
            </div>
          )}

          {/* Learning Materials */}
          {(learningMaterials.length > 0 || loadingMaterials) && (
            <div className="section">
              <h4>Learning Materials</h4>
              {loadingMaterials ? (
                <p style={{ color: '#888', fontStyle: 'italic' }}>Loading materials...</p>
              ) : (
                <div className="resources-list">
                  {learningMaterials.map((material, index) => (
                    <div key={index} className="resource-item">
                      <span className="resource-icon">{getMaterialTypeIcon(material.materialType)}</span>
                      <div>
                        <strong>{material.title || 'Untitled'}</strong>
                        {material.description && <p style={{ margin: '2px 0', fontSize: '13px', color: '#666' }}>{material.description}</p>}
                        {material.materialType === 'link' && material.externalLink ? (
                          <a href={material.externalLink} target="_blank" rel="noopener noreferrer" className="resource-link">
                            Open Link ↗
                          </a>
                        ) : material.fileURL ? (
                          <a href={material.fileURL} target="_blank" rel="noopener noreferrer" className="resource-link">
                            View File ↗
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Resources */}
          {assignment.resources && assignment.resources.length > 0 && (
            <div className="section">
              <h4>Resources</h4>
              <div className="resources-list">
                {assignment.resources.map((resource, index) => (
                  <div key={index} className="resource-item">
                    <span className="resource-icon">📎</span>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                      {resource.name || resource.url}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="print-button" onClick={() => window.print()}>
            🖨️ Print
          </button>
          <button className="close-footer-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignmentDetailView;
